import { sendMessage } from 'utils/webSocket';
import { request } from './util';

/*
 * Login
 */
export async function accountLogin(data) {
  return request({
    scope: 'portal',
    name: 'auth/login',
    data,
  });
}

export async function doAutoLogin(data) {
  return request({
    scope: 'portal',
    name: 'auth/auto-login',
    data,
  });
}

/**
 *
 * @returns {Promise<Boolean>}
 */
export async function checkAccount(token) {
  const result = await request({
    scope: 'portal',
    name: 'auth/check-account',
    data: { token },
  });

  if (!result) {
    throw new Error('unauthorized');
  }

  return true;
}

export function deleteToken() {
  sendMessage({
    scope: 'portal',
    name: 'auth/logout',
    data: {
      payload: {
        userId: localStorage.getItem('userId'),
        userName: localStorage.getItem('username'),
      },
    },
  });

  localStorage.removeItem('username');
  localStorage.removeItem('autoLogin');
  localStorage.removeItem('token');
  localStorage.removeItem('password');
}

/*
 * Logout
 */
export function accountLogout() {
  deleteToken();

  sessionStorage.setItem('logout', true);

  window.location = '/portal/auth/login';
}

/**
 *
 * @param {String} userId
 * @returns {Promise<{type: String, message: String, description: String}>}
 */
export async function requestResetPassword(userId) {
  return request({
    scope: 'portal',
    name: 'auth/send-password-reset-mail',
    data: {
      userId,
    },
  });
}

/*
 * 토큰 확인 후 패스워드 변경
 */
export function updatePasswordWithResetToken(token, password) {
  return request({
    scope: 'portal',
    name: 'auth/update-password-with-reset-token',
    data: {
      resetToken: token,
      password,
    },
  });
}

/**
 * 비밀번호 변경(현재 비밀번호 받음)
 * @param {{ userId: string, password: string(암호화), currentPassword: string(암호화)}} payload 전달할 값
 */
export async function changePassword(payload) {
  return request({
    scope: 'portal',
    name: 'auth/change-password',
    data: payload,
  });
}

/**
 * 비밀번호 변경(현재 비밀번호를 받지 않음) = 인증 후
 * ? auth/change-password와 다른 점은 현재 비밀번호를 받지 않는다.
 * ! 본 API는 대체 인증(SMS/이메일) 등으로 신원 확인 후에만 사용할 수 있다.
 * @param {{ userId: string, password: string(암호화)}} payload 전달할 값
 */
export async function changePasswordAfterAuth(payload) {
  return request({
    scope: 'portal',
    name: 'auth/change-password-after-2fa',
    data: payload,
  });
}

export async function getConnHistory(current, pagingParams) {
  return request({
    scope: 'portal',
    name: 'auth/get-connection-history',
    data: { vdGroupId: current.id, vdId: current.vdId, paging: { params: pagingParams } },
  });
}

export async function getRecentConnectionDate(data) {
  return request({
    scope: 'portal',
    name: 'auth/get-recent-connection-date',
    data,
  });
}

/**
 * 2FA 채널에 인증코드 발송 요청
 * @param {{userId: string, channel: string, action: 'auth' | 'reset-password', ... }} data
 * @returns ??
 */
export async function twoFactorCodeGenerate({ loginInfo, ...rest }) {
  return request({
    scope: 'portal',
    name: 'auth/generate-addtional-auth-code',
    data: {
      ...rest,
      ...loginInfo,
    },
  });
}

// 2factor 인증코드 검증
export async function twoFactorAuth(data) {
  return request({
    scope: 'portal',
    name: 'auth/do-additional-auth',
    data,
  });
}

// 2factor OTP 설정 키 저장
export async function saveOtpSecretkey(payload) {
  return request({
    scope: 'portal',
    name: 'auth/save-otp-secretkey',
    data: payload,
  });
}

export async function getRSAKey() {
  return request({
    scope: 'portal',
    name: 'auth/get-rsa-key',
    data: {},
  });
}

// * 생체인증 로직 타는 경우에만 userId 전송
export async function checkPreAuth(userId) {
  return request({
    scope: 'portal',
    name: 'auth/pre-check',
    data: {
      userId,
    },
  });
}

// #186818 하나은행 펜타시큐리티 생체 인증 사용자 정보 조회
export async function getBioAuthUser(userId) {
  return request({
    scope: 'portal',
    name: 'auth/check-bio-auth-target',
    data: {
      userId,
    },
  });
}

// 비밀번호 재설정시 인증 채널 목록을 가져온다.
export async function getPasswordResetChannel() {
  return request({
    scope: 'portal',
    name: 'auth/get-password-reset-auth-channel',
    // data
  });
}

// 비밀번호 재설정시 인증 코드 생성을 요청한다.
export async function generatePasswordResetCode(data) {
  return request({
    scope: 'portal',
    name: 'auth/generate-password-reset-code',
    data,
  });
}

// 비밀번호 재설정시 인증 코드 생성을 요청한다.
export async function checkPasswordResetCode(data) {
  return request({
    scope: 'portal',
    name: 'auth/check-password-reset-code',
    data,
  });
}

/**
 * 2FA 인증 채널 목록 요청
 * @param {{action: 'auth' | 'reset-password'}} data
 * @returns { channel: string[]}
 */
export async function getAdditionalAuthChannel(data) {
  return request({
    scope: 'portal',
    name: 'auth/get-additional-auth-channel',
    data,
  });
}

/**
 * 사용자의 연락처 정보
 * @param {{userId: string}} data
 * @returns { userName: string || null, telephoneNumber: string || null, mail: string || null, empCode: string || null }
 */
export async function getUserContact(data) {
  return request({
    scope: 'portal',
    name: 'auth/get-user-contact',
    data,
  });
}

/**
 * 비밀번호 재설정 요청하기
 * @param { userId: string, otpNumber: string, locale: 'ko-KR' | 'en-US', channel: 'kakaotalk' | 'email', empCode: string } data
 * @returns { success: boolean, msg: string }
 */
export async function doResetPassword(data) {
  return request({
    scope: 'portal',
    name: 'auth/reset-password',
    data,
  });
}
